/**
 * What the case agent proposed for setup — the subjects it read from the
 * request, the report type, the price — waiting on the analyst: applied as
 * their own, or dismissed, through the Assistant's proposals.
 */

import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';

import { vnextApiClient } from '@/services/vnext-api';
import type { SetupProposal } from '@/types/workbench';
import { useSetupActions } from './setupActions';

type Raw = Record<string, unknown>;
const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

function Details({ proposal }: Readonly<{ proposal: SetupProposal }>) {
  const { payload } = proposal;
  switch (proposal.kind) {
    case 'subjects': {
      const subjects = (payload.subjects as Raw[] | undefined) ?? [];
      return (
        <Stack gap={2}>
          {subjects.map((subject, index) => (
            <Text key={index} size="xs" data-testid={`proposal-subject-${index}`}>
              {text(subject.name)}
              {subject.subject_type ? ` · ${text(subject.subject_type)}` : ''}
              {Array.isArray(subject.jurisdictions) && subject.jurisdictions.length > 0
                ? ` · ${subject.jurisdictions.map(text).join(', ')}`
                : ''}
              {subject.role ? ` · ${text(subject.role).replace(/_/g, ' ')}` : ''}
              {subject.reference ? ` · ref ${text(subject.reference)}` : ''}
              {subject.where ? (
                <Text span size="xs" c="dimmed">
                  {' '}
                  (from {text(subject.where)})
                </Text>
              ) : null}
            </Text>
          ))}
        </Stack>
      );
    }
    case 'report_type':
      return (
        <Text size="xs">
          <strong>{text(payload.code)}</strong>
          {payload.reason ? ` — ${text(payload.reason)}` : ''}
        </Text>
      );
    case 'price':
      return (
        <Text size="xs">
          <strong>
            {text(payload.amount)} {text(payload.currency)}
          </strong>
          {payload.turnaround_days !== undefined && payload.turnaround_days !== null
            ? ` · ${text(payload.turnaround_days)} days`
            : ''}
          {payload.reason ? ` — ${text(payload.reason)}` : ''}
        </Text>
      );
    default:
      return null;
  }
}

const KIND: Record<string, string> = {
  subjects: 'Subjects',
  report_type: 'Report type',
  price: 'Price',
};

export function SetupProposals({
  proposals,
  kinds,
}: Readonly<{ proposals: SetupProposal[]; kinds: string[] }>) {
  const actions = useSetupActions();
  const shown = proposals.filter((p) => kinds.includes(p.kind));
  if (shown.length === 0) {
    return null;
  }
  return (
    <Stack gap="xs" data-testid="setup-proposals">
      {shown.map((proposal) => (
        <Card
          key={proposal.id}
          withBorder
          padding="xs"
          radius="sm"
          data-testid={`proposal-${proposal.id}`}
        >
          <Stack gap={6}>
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Text size="xs" fw={600}>
                {KIND[proposal.kind] ?? proposal.kind} proposed: {proposal.summary}
              </Text>
              <Badge size="xs" variant="light" color="violet">
                The case agent
              </Badge>
            </Group>
            <Details proposal={proposal} />
            {actions.editable ? (
              <Group gap="xs" justify="flex-end">
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  loading={actions.pending === `dismiss:${proposal.id}`}
                  onClick={() =>
                    void actions.run(`dismiss:${proposal.id}`, () =>
                      vnextApiClient.dismissCopilotProposal(actions.reportId, proposal.id)
                    )
                  }
                  data-testid={`proposal-dismiss-${proposal.id}`}
                >
                  Dismiss
                </Button>
                <Button
                  size="compact-xs"
                  loading={actions.pending === `apply:${proposal.id}`}
                  onClick={() =>
                    void actions.run(
                      `apply:${proposal.id}`,
                      () => vnextApiClient.applyCopilotProposal(actions.reportId, proposal.id),
                      'Applied as your own.'
                    )
                  }
                  data-testid={`proposal-apply-${proposal.id}`}
                >
                  Use it
                </Button>
              </Group>
            ) : null}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
